<template>
  <div
    ref="pickerRef"
    class="vm-search-select"
    data-vue-managed="true"
    :data-vue-open="open ? 'true' : 'false'"
  >
    <input
      ref="hiddenInputRef"
      :name="inputName"
      type="hidden"
      :class="inputClassNames"
      :value="selectedValue"
      v-bind="inputData"
    >
    <button
      v-if="!readonly"
      type="button"
      class="form-control vm-search-select-trigger"
      :aria-expanded="open ? 'true' : 'false'"
      @click.stop="togglePicker"
    >
      <span class="vm-search-select-trigger-label">{{ selectedLabel }}</span>
      <span class="caret"></span>
    </button>
    <button v-else type="button" class="form-control vm-search-select-trigger is-readonly">
      <span class="vm-search-select-trigger-label">{{ selectedLabel }}</span>
      <span class="caret"></span>
    </button>
  </div>
  <teleport to="body">
    <div
      v-if="open && !readonly"
      ref="panelRef"
      class="vm-search-select-panel"
      :style="panelStyle"
      @click.stop
    >
      <input
        v-model="filterKeyword"
        type="text"
        class="form-control vm-search-select-input"
        :placeholder="filterPlaceholder"
      >
      <div class="vm-search-select-options">
        <button
          v-for="option in filteredOptions"
          :key="`vm-nic-option-${option.value}`"
          type="button"
          class="vm-search-select-option"
          :class="{ 'is-selected': selectedValue === option.value }"
          @click="selectOption(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  initialValue: {
    type: [String, Number],
    default: ""
  },
  inputName: {
    type: String,
    required: true
  },
  inputClasses: {
    type: [String, Array],
    default: () => ["vm-search-select-value"]
  },
  inputData: {
    type: Object,
    default: () => ({})
  },
  emptyLabel: {
    type: String,
    default: "Select an option"
  },
  filterPlaceholder: {
    type: String,
    default: "Filter options"
  },
  readonly: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(["update:value"])

const pickerRef = ref(null)
const panelRef = ref(null)
const hiddenInputRef = ref(null)
const open = ref(false)
const filterKeyword = ref("")
const selectedValue = ref(String(props.initialValue || ""))
const pickerId = `vm-nic-select-${Math.random().toString(36).slice(2)}`
const panelStyle = ref({})

const inputClassNames = computed(() =>
  Array.isArray(props.inputClasses) ? props.inputClasses.join(" ") : props.inputClasses
)

const normalizedOptions = computed(() =>
  (props.options || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const filteredOptions = computed(() => {
  const keyword = filterKeyword.value.trim().toLowerCase()
  if (!keyword) return normalizedOptions.value
  return normalizedOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})

const selectedLabel = computed(() => {
  return normalizedOptions.value.find((option) => option.value === selectedValue.value)?.label || props.emptyLabel
})

function togglePicker() {
  const nextOpen = !open.value
  if (nextOpen) {
    document.dispatchEvent(new CustomEvent("scc:close-vm-pickers", {
      detail: { sourceId: pickerId }
    }))
  }

  open.value = nextOpen
  if (!open.value) {
    filterKeyword.value = ""
  } else {
    nextTick(() => {
      updatePanelPosition()
    })
  }
}

function selectOption(value) {
  selectedValue.value = String(value)
  open.value = false
  filterKeyword.value = ""
}

function closeOnOutsideClick(event) {
  if (pickerRef.value && pickerRef.value.contains(event.target)) {
    return
  }

  if (panelRef.value && panelRef.value.contains(event.target)) {
    return
  }

  if (open.value) {
    open.value = false
    filterKeyword.value = ""
  }
}

function closeOnGlobalEvent(event) {
  if (event.detail && event.detail.sourceId === pickerId) {
    return
  }

  open.value = false
  filterKeyword.value = ""
}

function updatePanelPosition() {
  if (!pickerRef.value) {
    return
  }

  const rect = pickerRef.value.getBoundingClientRect()
  panelStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 6}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`
  }
}

function handleViewportChange() {
  if (!open.value) {
    return
  }

  updatePanelPosition()
}

function dispatchHiddenChange() {
  if (!hiddenInputRef.value) {
    return
  }

  hiddenInputRef.value.dispatchEvent(new Event("change", { bubbles: true }))
}

watch(selectedValue, async () => {
  await nextTick()
  emit("update:value", selectedValue.value)
  dispatchHiddenChange()
})

watch(() => props.initialValue, (value) => {
  selectedValue.value = String(value || "")
})

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick)
  document.addEventListener("scc:close-vm-pickers", closeOnGlobalEvent)
  window.addEventListener("resize", handleViewportChange)
  window.addEventListener("scroll", handleViewportChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
  document.removeEventListener("scc:close-vm-pickers", closeOnGlobalEvent)
  window.removeEventListener("resize", handleViewportChange)
  window.removeEventListener("scroll", handleViewportChange, true)
})
</script>
